import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from 'lucide-react';
import { authClient, signOut } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export default function PasskeyVerification(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async (): Promise<void> => {
    setIsVerifying(true);
    setError(null);

    try {
      const result = await authClient.signIn.passkey();

      if (result?.error) {
        setError(result.error.message || 'Passkey verification failed');
        return;
      }

      // Mark 2FA as verified for this tab session
      sessionStorage.setItem('twoFactorVerified', 'true');

      await authClient.getSession({ fetchOptions: { credentials: 'include' } });
      await queryClient.invalidateQueries();
      navigate({ to: '/' });
    } catch {
      setError('Passkey verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    sessionStorage.removeItem('twoFactorVerified');
    await signOut();
    // Clear (not just invalidate) — any query tied to the signed-in
    // user (preferences, me, permissions) must be evicted so a
    // subsequent login doesn't read prior-user data from cache.
    queryClient.clear();
    navigate({ to: '/login' });
  };

  return (
    <div className="flex h-full overflow-y-auto bg-background p-4">
      <Card className="m-auto w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Fingerprint className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Verify your identity</CardTitle>
          <CardDescription>
            Use your passkey to complete sign in. This adds an extra layer of security to your
            account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}
          <Button className="w-full" onClick={handleVerify} disabled={isVerifying}>
            <Fingerprint className="mr-2 h-4 w-4" />
            {isVerifying ? 'Verifying...' : 'Verify with Passkey'}
          </Button>
        </CardContent>
        <CardFooter>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
          >
            Sign out and use a different account
          </button>
        </CardFooter>
      </Card>
    </div>
  );
}
